import { type JSX, useEffect, useReducer } from "react";

import { LeadsMobileList } from "features/leads/components";
import { DEFAULT_LEADS_SEARCH_PARAMS } from "features/leads/constants";
import { useLeads, useLeadsInfo } from "features/leads/datahooks";
import useLeadsSearchParams from "features/leads/hooks/useLeadsSearchParams";
import type { LeadsSearchParams } from "features/leads/types";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import PaginationFooter from "common/components/PaginationFooter";
import usePagination from "common/hooks/usePagination";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SelectedFacetParam } from "common/types";

import ScrollAndFooter from "../ScrollAndFooter";
import SearchAndFiltersEmptyPlaceholder from "../SearchAndFiltersEmptyPlaceholder";
import DesktopTable from "./DesktopTable";
import DesktopFilters from "./Filters/DesktopFilters";
import MobileFilters from "./Filters/MobileFilters";
import SearchLeads from "./SearchLeads";

import type { DripLeadListSource } from "../../../types";

export default function Leads({
  createSource,
  isCreatingSource,
}: {
  createSource: (params: { settings: DripLeadListSource["settings"] }) => void;
  isCreatingSource: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const { page, setPage, pageSize, setPageSize } =
    usePagination("leads-source");

  const [searchParams, setSearchParams] = useReducer(
    (
      prevState: LeadsSearchParams<SelectedFacetParam>,
      newState: Partial<LeadsSearchParams<SelectedFacetParam>>,
    ) => ({
      ...prevState,
      ...newState,
    }),
    DEFAULT_LEADS_SEARCH_PARAMS,
  );

  // any change to the search params invalidates the current page
  useEffect(() => {
    setPage(0);
  }, [searchParams]);

  const { hasFilters, resetParams } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );

  const { leadsInfo } = useLeadsInfo(searchParams);
  const { leads, isFetchingLeads, leadsError, refetchLeads } = useLeads(
    searchParams,
    { order: "desc", pageSize, page },
  );

  const searchComponent = (
    <SearchLeads setKeywords={(keywords) => setSearchParams({ keywords })} />
  );

  const totalLeads = leadsInfo ? leadsInfo.totalLeads : 0;
  let content: JSX.Element;

  if (leadsError) {
    content = (
      <ErrorPlaceholder
        errorMessage="Error loading leads"
        onRetry={refetchLeads}
      />
    );
  } else if (isFetchingLeads || leads?.length) {
    content = (
      <div className="flex flex-1 flex-col gap-y-4 overflow-hidden">
        {isDesktop ? (
          <DesktopTable
            leads={leads}
            isLoading={isFetchingLeads}
            pageSize={pageSize}
            totalItemCount={totalLeads}
            searchComponent={searchComponent}
          />
        ) : (
          <LeadsMobileList
            leads={leads}
            isLoading={isFetchingLeads}
            pageSize={pageSize}
            totalItemCount={totalLeads}
          />
        )}
        <PaginationFooter
          pageSize={pageSize}
          setPageSize={setPageSize}
          selectedPage={page}
          setSelectedPage={setPage}
          totalItemCount={totalLeads}
          isLastPageHidden
        />
      </div>
    );
  } else {
    content = (
      <SearchAndFiltersEmptyPlaceholder
        hasSearch={!!searchParams.keywords}
        hasFilters={hasFilters}
        onClearAll={resetParams}
      />
    );
  }

  const FiltersComponent = isDesktop ? DesktopFilters : MobileFilters;

  return (
    <ScrollAndFooter
      leadCount={totalLeads}
      isCreatingSource={isCreatingSource}
      onCreate={() =>
        createSource({ settings: { type: "drip", ...searchParams } })
      }
    >
      <div className="flex gap-4 lg:flex-row flex-col grow">
        <div className="flex lg:flex-col gap-2 lg:gap-2.5 lg:w-60 lg:shrink-0">
          {!isDesktop && searchComponent}
          <FiltersComponent
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        </div>

        {content}
      </div>
    </ScrollAndFooter>
  );
}
